import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useLang } from '../context/LangContext';
import InvoiceForm from '../components/InvoiceForm';

export default function InvoiceUploadPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [conns, setConns]   = useState([]);
  const [parsing, setParsing] = useState(false);
  const [error, setError]   = useState('');
  const [result, setResult] = useState(null);   // { invoices:[{suggestion,confidence}], meta }
  const [doneIdx, setDoneIdx] = useState([]);    // opgeslagen indexen

  useEffect(() => { api.get('/api/connections').then(r => setConns(r.data)); }, []);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true); setError(''); setResult(null); setDoneIdx([]);
    const fd = new FormData();
    fd.append('pdf', file);
    try {
      const res = await api.post('/api/invoices/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || t('error'));
    } finally {
      setParsing(false);
    }
  }

  // Koppel geparste EAN/energietype aan een bestaande aansluiting
  function guessConnection(s) {
    if (s.ean_code) {
      const byEan = conns.find(c => c.ean_code && c.ean_code === s.ean_code);
      if (byEan) return byEan.id;
    }
    if (s.energy_type) {
      const byType = conns.find(c => c.energy_type === s.energy_type);
      if (byType) return byType.id;
    }
    return conns[0]?.id ?? '';
  }

  function markDone(idx) {
    const next = [...doneIdx, idx];
    setDoneIdx(next);
    if (result && next.length >= result.invoices.length) navigate('/invoices');
  }

  return (
    <>
      <div className="page-header">
        <h2>{t('upload_title')}</h2>
        <button className="btn btn-link btn-sm text-muted" onClick={() => navigate('/invoices')}>
          <i className="bi bi-arrow-left me-1" />{t('back')}
        </button>
      </div>

      <div className="page-body" style={{ maxWidth: 920 }}>
        {conns.length === 0 && (
          <div className="alert alert-warning">
            Maak eerst een aansluiting aan onder <strong>{t('nav_connections')}</strong>.
          </div>
        )}

        {!result && (
          <div className="card">
            <div className="card-body text-center py-5">
              {parsing ? (
                <div className="text-muted">
                  <div className="spinner-border text-primary mb-3" />
                  <div>{t('upload_parsing')}</div>
                </div>
              ) : (
                <>
                  <i className="bi bi-file-earmark-arrow-up text-primary" style={{ fontSize: 40 }} />
                  <div className="my-3 text-muted">{t('upload_choose')}</div>
                  <label className="btn btn-primary">
                    <i className="bi bi-upload me-1" />{t('inv_upload')}
                    <input type="file" accept="application/pdf" hidden
                      onChange={handleFile} disabled={conns.length === 0} />
                  </label>
                  {error && <div className="alert alert-danger mt-3 mb-0 py-2">{error}</div>}
                </>
              )}
            </div>
          </div>
        )}

        {result && (
          <>
            <div className="alert alert-info d-flex justify-content-between align-items-center">
              <span><i className="bi bi-info-circle me-2" />{t('upload_hint')}</span>
              <span className="text-muted-sm">
                {result.invoices.length} {result.invoices.length > 1 ? 'facturen' : 'factuur'} ·{' '}
                {t('upload_method')}: {result.meta.method}
              </span>
            </div>

            {result.invoices.map((inv, idx) => {
              if (doneIdx.includes(idx)) {
                return (
                  <div key={idx} className="alert alert-success py-2">
                    <i className="bi bi-check-circle me-2" />
                    {t(`type_${inv.suggestion.energy_type}`)} — opgeslagen
                  </div>
                );
              }
              return (
                <div key={idx} className="mb-4">
                  <div className="d-flex align-items-center mb-2">
                    <span className={`badge badge-${inv.suggestion.energy_type} me-2`}>
                      {t(`type_${inv.suggestion.energy_type}`)}
                    </span>
                    {result.invoices.length > 1 && (
                      <span className="text-muted-sm">Factuur {idx + 1} van {result.invoices.length}</span>
                    )}
                    {inv.confidence != null && (
                      <span className="text-muted-sm ms-auto">
                        {t('upload_confidence')}: {Math.round(inv.confidence * 100)}%
                      </span>
                    )}
                  </div>
                  <InvoiceForm
                    connections={conns}
                    initial={{ ...inv.suggestion, connection_id: guessConnection(inv.suggestion) }}
                    meta={{
                      source: 'pdf',
                      parse_status: 'verified',
                      pdf_filename: result.meta.pdf_filename,
                      raw_json: inv.suggestion,
                    }}
                    onSaved={() => markDone(idx)}
                  />
                </div>
              );
            })}
          </>
        )}
      </div>
    </>
  );
}
